
import {
  Mixins,
  Component,
  Provide,
  Prop,
  Inject
} from "vue-property-decorator";
import { mapState } from "vuex";
import { convertObjAb2Json, downloadBlob } from "@/helpers/file";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import Context from "@/mixins/context";
import BrandsStore from "@/mixins/brandsStore";
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { UUID } from "@/data/enums";
import { ClientRoutes } from "@/data/enums/router";

// Types
import { IProvider } from "@/models/providers";
import {
  ITicketProvider,
  ITicketProviderMethods
} from "@/models/providers/ticket";
import { IUser } from "@/models/users";
import { ISocketMessage } from "@/models/socket";
import { ITicketsProviderMethods } from "@/models/providers/tickets";
import { IState } from "@/store";
import { IStatus } from "@/models/statuses";
import { ITicket, ITicketDepartment } from "@/models/tickets";
import { IContractProduct } from "@/models/contracts";
import { IActionControl } from "@/models/ui";

@Component({
  computed: {
    ...mapState({
      ticketStatuses: (state: IState) => state.constants.statuses.ticket,
      ticketScope(state: IState, getters) {
        return getters[`data/${DataModules.TICKETS}/scope`](this.ticketId);
      },
      ticket(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.TICKETS,
            scope: this.ticketScope
          }) || {}
        );
      }
    })
  }
})
export default class TicketProvider
  extends Mixins(Context, BrandsStore, RequestPromises)
  implements IProvider {
  // Inject

  @Inject({ from: "ticketsProviderMethods", default: null })
  public readonly $ticketsMethods!: ITicketsProviderMethods;

  // Props

  @Prop({ required: true })
  public ticketId!: ITicket["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({
    default: () => (vm: TicketProvider) => {
      const name = vm.isAdmin
        ? "adminSupportInbox"
        : ClientRoutes.SUPPORT_TICKETS;
      return vm.$router.replace({ name }).catch(err => err);
    }
  })
  public onNotFound!: IProvider["onNotFound"];

  // Data

  public hasRequestError = false;
  public isDownloadingEmailFile = false;
  public isLoading = false;
  public isProcessing = false;
  public isReloading = false;
  public ticket!: ITicket;
  public ticketDepartments: ITicketDepartment[] = [];
  public ticketScope!: string;
  public ticketStatuses!: IStatus[];

  // Computed

  public get ticketStatus(): IStatus {
    if (!this.ticket) {
      return {} as IStatus;
    }
    const status = _.find(
      this.ticketStatuses,
      s => s.id === _.get(this.ticket, "status_id")
    );
    return { code: "", ...status } as IStatus;
  }

  public get participants() {
    // Order with lead agent first
    return _.orderBy(this.ticket.users, ["pivot.default"], ["desc"]);
  }

  public get watchers() {
    return _.filter(
      this.ticket.users,
      user => user.id !== _.get(this.leadAgent, "id")
    );
  }

  public get leadAgent() {
    return _.get(this.ticket, "lead_user", null);
  }

  public getTicketDepartmentName(ticket: ITicket) {
    return this.$store.getters[
      `data/${DataModules.SUPPORT_DEPARTMENTS}/getDepartmentName`
    ](
      _.find(this.ticketDepartments, [
        "id",
        _.get(ticket, "ticket_department_id")
      ]),
      ticket.brand_id
    );
  }

  public get department() {
    return this.getTicketDepartmentName(this.ticket);
  }

  public get isStaged() {
    return !!this.ticket.staged_import;
  }

  public get isClosed() {
    return _.get(this.ticketStatus, "code", "") === "ticket_closed";
  }

  public get isClientTicket() {
    return !!_.get(this.ticket, "client_id");
  }

  public get isLeadTicket() {
    return !!_.get(this.ticket, "lead_id");
  }

  public get isDelegatedAccess() {
    return !!_.get(this.ticket, "is_delegated_object");
  }

  public get contractProduct() {
    return _.get(this.ticket, "contract_product", {}) as IContractProduct;
  }

  public get canUpdateTicketMessage() {
    return this.$userCan("update_ticket_message");
  }

  public get canDeleteTicketMessage() {
    return this.$userCan("destroy_ticket_message");
  }

  public get condition() {
    return this.$store.getters[`data/${DataModules.TICKETS}/statusSummary`](
      this.ticket
    );
  }

  public get actions(): IActionControl[] {
    return _.filter(
      [
        {
          execute: this.openTicket,
          id: "reopen",
          isDisabled: false,
          isVisible: this.isClosed,
          label: this.$t("_action.reopen_ticket").toString(),
          tag: "b-button"
        },
        {
          execute: this.openChangeSubjectModal,
          id: "edit_subject",
          isDisabled: false,
          isVisible: !this.isClosed,
          label: this.$t("_action.edit_subject").toString(),
          icon: "pen",
          tag: "b-button"
        },
        {
          execute: this.openDelegateAccessModal,
          id: "delegate",
          isDisabled: false,
          isVisible: !this.isClosed && !this.ticket.is_delegated_object,
          label: this.$t("_action.delegate_access").toString(),
          icon: "user-friends",
          tag: "b-button"
        },
        {
          execute: this.openSelectProductModal,
          id: "select",
          isVisible: !this.isClosed && !this.hasRelatedProduct,
          label: this.$t("_action.add_related_product").toString(),
          tag: "b-button",
          type: "is-outlined"
        },
        {
          execute: this.openSelectProductModal,
          id: "change",
          isVisible: !this.isClosed && this.hasRelatedProduct,
          label: this.$t("_action.change_related_product").toString(),
          tag: "b-button",
          icon: "link",
          type: "is-outlined"
        },
        {
          execute: this.unlinkProduct,
          id: "unlink",
          isVisible: !this.isClosed && this.hasRelatedProduct,
          label: this.$t("_action.remove_product_relation").toString(),
          tag: "b-button",
          icon: "unlink",
          type: "is-danger is-outlined"
        },
        {
          execute: this.closeTicket,
          id: "close",
          isDisabled: false,
          isVisible: !this.isClosed,
          label: this.$t("_action.close_ticket").toString(),
          tag: "b-button",
          classes: "has-text-danger",
          hasSeparator: true,
          type: "is-danger is-outlined"
        }
      ],
      ["isVisible", true]
    );
  }

  public get hasRelatedProduct() {
    return !!this.contractProduct;
  }

  // Methods

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await this.getTicket(ignoreStored);
      const [ticketDepartments] = await Promise.all([
        this.$store.dispatch("constants/getDepartments", {
          fromCache:
            !this.isMockClientContext &&
            !this.$store.getters["brand/isMultibrand"],
          params: _.pick(this.ticket, ["brand_id"])
        }),
        this.$store.dispatch("constants/getStatuses", "ticket")
      ]);
      this.ticketDepartments = ticketDepartments;
      this.hasRequestError = false;
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public getTicket(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.TICKETS}/get`, {
      id: this.ticketId,
      ignoreStored,
      scope: this.ticketScope,
      vm: this,
      params: {
        with_staged_imports: 1,
        with: _.union(
          [
            "account",
            "brand",
            "brand.image",
            "client",
            "client.image",
            "contract_product",
            "contract_product.product.image",
            "delegates",
            "delegates.client",
            "delegates.client.image",
            "department",
            "import.credentials",
            "import.source",
            "lead",
            "lead_user.image",
            "status",
            "user",
            "users",
            "users.image"
          ],
          this.$store.getters.isAdminContext &&
            this.$store.getters.isUpmindContext
            ? ["client.upmind_org"]
            : []
        ).join(",")
      }
    });
  }

  public updateStoreTicket(data) {
    this.$store.commit(`data/updateData`, {
      storeModule: DataModules.TICKETS,
      scope: this.ticketScope,
      data
    });
  }

  public updateStoreListItem(ticket = this.ticket) {
    if (!this.$ticketsMethods) return;
    this.$nextTick(() => {
      this.$ticketsMethods.updateStoreListItem(ticket);
    });
  }

  public setTicketSubjectBreadcrumb() {
    this.$store.commit("ui/breadcrumb/add", {
      key: "ticket-subject",
      value: this.ticket.subject
    });
  }

  public openDeleteMessageModal(message) {
    return new Promise(resolve => {
      this.$store.dispatch(
        `data/${DataModules.TICKETS_MESSAGES}/openDeleteModal`,
        {
          props: {
            ticketId: this.ticketId,
            ticketMessageId: message.id,
            isClientMessage: message.isClient
          },
          events: {
            success: ({ ticketMessage }) => {
              resolve({ ticketMessage });
            }
          }
        }
      );
    });
  }

  public openAssignUserModal() {
    return this.$store.dispatch("ui/open/slideModal", {
      config: {
        parent: this, // Parent required for Inject() to work
        component: () =>
          import("@/components/app/global/tickets/manageAssigneesModal.vue")
      }
    });
  }

  public openDelegateAccessModal() {
    return this.$store.dispatch("ui/open/modal", {
      config: {
        parent: this, // Parent required for Inject() to work
        component: () =>
          import("@/components/app/global/tickets/delegateAccessModal.vue")
      }
    });
  }

  public assignToSelf() {
    const self = _.get(this.$store.state, "user.id");
    const users = _.union([self], _.map(_.get(this.ticket, "users", []), "id"));
    return this.assignUsers(
      _.map(users, (userId: IUser["id"]) => ({
        id: userId,
        default: userId === self
      }))
    );
  }

  public setToUnassigned() {
    const users = _.get(this.ticket, "users", []);
    return this.assignUsers(
      _.map(users, (user: IUser) => ({
        id: user.id,
        default: false
      }))
    );
  }

  public async assignUsers(users: { id: IUser["id"]; default?: boolean }[]) {
    if (this.isProcessing) return;
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.TICKETS}/assignUsers`, {
        ticketId: this.ticketId,
        users
      });
      await this.getTicket(true);
      if (this.$ticketsMethods) this.$ticketsMethods.refreshInbox();
      this.$buefy.toast.open({
        message: this.$t("_sentence.ticket.participants_updated") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public openChangeSubjectModal() {
    return this.$store.dispatch("ui/open/windowModal", {
      config: {
        parent: this, // Parent required for Inject() to work
        component: () =>
          import(
            "@/components/app/global/tickets/changeTicketSubjectModal.vue"
          ),
        width: 480
      }
    });
  }

  public async updateTicket(data: Record<string, any>) {
    try {
      const ticket = await this.$store.dispatch(
        `data/${DataModules.TICKETS}/update`,
        {
          id: this.ticket.id,
          data,
          scope: this.ticketScope
        }
      );
      this.getTicket(true);
      this.updateStoreListItem(ticket);
      return ticket;
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  public async changeSubject(subject) {
    await this.updateTicket({ subject });
    this.$buefy.toast.open({
      message: this.$t("_.subject_changed") as string
    });
  }

  public async changeStatus(statusId: IStatus["id"]) {
    if (this.isProcessing) return;
    try {
      this.isProcessing = true;
      await this.$store.dispatch(`data/${DataModules.TICKETS}/changeStatus`, {
        id: this.ticket.id,
        statusId
      });
      await this.getTicket(true);
      if (this.$ticketsMethods) this.$ticketsMethods.refreshInbox();

      this.$buefy.toast.open({
        message: this.$t("_sentence.ticket.status_changed") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public async changeDepartment(departmentId: ITicketDepartment["id"]) {
    if (this.isProcessing) return;

    try {
      this.isProcessing = true;
      const ticket = await this.$store.dispatch(
        `data/${DataModules.TICKETS}/update`,
        {
          id: this.ticket.id,
          data: { ticket_department_id: departmentId },
          scope: this.ticketScope
        }
      );
      this.updateStoreListItem(ticket);
      if (this.$ticketsMethods) this.$ticketsMethods.refreshInbox();
      this.$buefy.toast.open({
        message: this.$t("_sentence.ticket.department_changed") as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isProcessing = false;
    }
  }

  public openTicket() {
    const statusId = _.get(
      _.find(this.ticketStatuses, ["code", "ticket_open"]),
      "id"
    ) as IStatus["id"];
    return this.changeStatus(statusId);
  }

  public closeTicket() {
    const statusId = _.get(
      _.find(this.ticketStatuses, ["code", "ticket_closed"]),
      "id"
    ) as IStatus["id"];
    return this.changeStatus(statusId);
  }

  public setupSocketListeners() {
    // Listen for ticket subject update
    this.$bus.$on("socket-ticket-update", async (data: ISocketMessage) => {
      const { subject, ticket_id } = JSON.parse(data.message);
      if (ticket_id !== this.ticketId) return; // Different ticket
      if (this.ticket.subject === subject) return; // Unchanged subject
      this.updateStoreTicket({ subject });
    });
    // Listen for ticket status update
    this.$bus.$on(
      "socket-ticket-status-update",
      async (data: ISocketMessage) => {
        const { status_code, ticket_id } = JSON.parse(data.message);
        const ticketStatusCode = _.get(this.ticket, "status.code");
        if (ticket_id !== this.ticketId) return; // Different ticket
        if (ticketStatusCode === status_code) return; // Unchanged status
        const status = _.find(this.ticketStatuses, [
          "code",
          status_code
        ]) as IStatus;
        this.updateStoreTicket({
          status_id: status.id,
          status
        });
      }
    );
  }

  public async downloadEmailFile(messageId = undefined) {
    try {
      if (this.isDownloadingEmailFile) return;
      this.isDownloadingEmailFile = true;
      const dataModule = messageId
        ? DataModules.TICKETS_MESSAGES
        : DataModules.TICKETS;
      const data = await this.$store.dispatch(
        `data/${dataModule}/downloadEmailFile`,
        {
          ticketId: this.ticketId,
          messageId
        }
      );
      const blob = new Blob([data], {
        type: `text/plain; charset="UTF-8"`
      });
      downloadBlob(blob, `${this.ticket.reference}.txt`);
    } catch (error) {
      // Convert ArrayBuffer data to JSON
      convertObjAb2Json(error as any, "response.data");
      // Handle error
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isDownloadingEmailFile = false;
    }
  }

  public openSelectProductModal() {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS}/openSelectClientProductModal`,
      {
        props: {
          accountId: this.ticket.account_id
        },
        events: {
          success: ({ id }: IContractProduct) =>
            this.updateTicket({
              contract_product_id: id
            })
        }
      }
    );
  }

  public unlinkProduct() {
    return this.updateTicket({ contract_product_id: null });
  }

  // Life-cycle methods

  public async created() {
    this.isLoading = true;
    await this.loadData();
    if (
      !this.isMockClientContext &&
      this.ticket.brand_id !== this.brandId &&
      this.brandId !== UUID.ORG
    ) {
      this.onNotFound && this.onNotFound(this);
    }
    this.setupSocketListeners();
    this.setTicketSubjectBreadcrumb();
  }

  public beforeDestroy() {
    this.$bus.$off("socket-ticket-update");
    this.$bus.$off("socket-ticket-status-update");
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.TICKETS,
      scope: this.ticketScope,
      vm: this
    });
  }

  // Provide

  @Provide("ticketProvider")
  public providerData(): ITicketProvider {
    return {
      actions: this.actions,
      canDeleteTicketMessage: this.canDeleteTicketMessage,
      canUpdateTicketMessage: this.canUpdateTicketMessage,
      contractProduct: this.contractProduct,
      delegates: this.ticket.delegates,
      department: this.department,
      hasRelatedProduct: this.hasRelatedProduct,
      hasRequestError: this.hasRequestError,
      isClientTicket: this.isClientTicket,
      isClosed: this.isClosed,
      isDelegatedAccess: this.isDelegatedAccess,
      isDownloadingEmailFile: this.isDownloadingEmailFile,
      isLeadTicket: this.isLeadTicket,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      isStaged: this.isStaged,
      leadAgent: this.leadAgent,
      participants: this.participants,
      ticket: this.ticket,
      ticketDepartments: this.ticketDepartments,
      ticketId: this.ticketId,
      ticketStatus: this.ticketStatus,
      ticketStatuses: this.ticketStatuses,
      watchers: this.watchers
    };
  }

  @Provide("ticketProviderMethods")
  public get providerMethods(): ITicketProviderMethods {
    return {
      assignToSelf: this.assignToSelf,
      assignUsers: this.assignUsers,
      changeStatus: this.changeStatus,
      changeDepartment: this.changeDepartment,
      changeSubject: this.changeSubject,
      closeTicket: this.closeTicket,
      getTicket: this.getTicket,
      openAssignUserModal: this.openAssignUserModal,
      openChangeSubjectModal: this.openChangeSubjectModal,
      openDelegateAccessModal: this.openDelegateAccessModal,
      openDeleteMessageModal: this.openDeleteMessageModal,
      openTicket: this.openTicket,
      setToUnassigned: this.setToUnassigned,
      updateTicket: this.updateTicket,
      downloadEmailFile: this.downloadEmailFile,
      reloadData: this.reloadData
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $ticketData: this.providerData(),
        $ticketMethods: this.providerMethods
      })
    );
  }
}
